import { nodeConfig } from '@scripts/mixins';
import {
	verticalKeyNavigationHandler,
	horizontalKeyNavigationHandler
} from '@scripts/helpers/key-navigation';
import { Disclosure } from '@scripts/modules/disclosure';

const NAMESPACE = 'accordion';
const DEFAULT_SELECTOR = '[data-js-accordion]';
const ITEM_SELECTOR = '[data-js-accordion-item]';

/**
 * @typedef {Object} AccordionSettings
 * @property {boolean} [hiddenUntilFound=true] - Enable search-in-page support.
 * @property {boolean} [deeplink=false] - Enable URL hash updates.
 * @property {boolean} [grouped=false] - Auto-close siblings (true Accordion behavior).
 * @property {boolean} [alwaysOpen=false] - One item is always open. Forces grouped=true.
 * @property {'vertical'|'horizontal'} [orientation='vertical'] - Navigation direction.
 */
const DEFAULT_SETTINGS = {
	hiddenUntilFound: true,
	deeplink: false,
	grouped: false,
	alwaysOpen: false,
	orientation: 'vertical'
};

/**
 * Accordion using Disclosure module.
 * @fires accordion:open
 * @fires accordion:close
 */
class Accordion {
	/**
	 * @param {HTMLElement} node
	 * @param {AccordionSettings} config
	 */
	constructor(node, config) {
		this.elNode = node;
		this.config = config;

		if (this.config.alwaysOpen) {
			this.config.grouped = true;
		}

		this.items = [];
	}

	init() {
		const itemNodes = this.elNode.querySelectorAll(ITEM_SELECTOR);

		if (!itemNodes.length) return;

		this.items = Array.from(itemNodes).map((node) => {
			const itemConfig = {
				namespace: NAMESPACE,
				hiddenUntilFound: this.config.hiddenUntilFound,
				deeplink: this.config.deeplink,
				collapsible: !this.config.alwaysOpen,
				keyboardNavigation: false,
				closeOnOutsideClick: false,
				closeOnBlur: false,
				closeOnEscape: false
			};

			const instance = new Disclosure(node, itemConfig);

			instance.init();

			return instance;
		});

		this.items = this.items.filter((item) => item.elButton && item.elPanel);

		if (!this.items.length) return;

		if (this.config.alwaysOpen) {
			const hasOpen = this.items.some((item) => item.isOpen());

			if (!hasOpen) {
				this.items[0].open(false);
			}

			this._updateAriaDisabled();
		}

		this._bindUI();
	}

	destroy() {
		this.items.forEach((item) => item.destroy());
		this._unbindUI();
	}

	/** Update settings (example: orientation changes). */
	updateConfig(newSettings) {
		this.config = { ...this.config, ...newSettings };
	}

	_bindUI() {
		if (this.config.grouped) {
			this._onChildOpen = this._onChildOpen.bind(this);
			this.elNode.addEventListener(`${NAMESPACE}:open`, this._onChildOpen);
		}

		this._onKeydown = this._onKeydown.bind(this);
		this.elNode.addEventListener('keydown', this._onKeydown);
	}

	_unbindUI() {
		if (this.config.grouped) {
			this.elNode.removeEventListener(`${NAMESPACE}:open`, this._onChildOpen);
		}

		this.elNode.removeEventListener('keydown', this._onKeydown);
	}

	/** Handle grouped (radio button style) logic and aria-disabled updates. */
	_onChildOpen(event) {
		const activeInstance = event.detail.instance;

		if (!this.items.includes(activeInstance)) return;

		this.items.forEach((item) => {
			if (item !== activeInstance && item.isOpen()) {
				item.close(false);
			}
		});

		if (this.config.alwaysOpen) {
			this._updateAriaDisabled();
		}
	}

	/** Set aria-disabled on the sole open item when alwaysOpen. */
	_updateAriaDisabled() {
		this.items.forEach((item) => {
			if (item.isOpen()) {
				item.elButton.setAttribute('aria-disabled', 'true');
			} else {
				item.elButton.removeAttribute('aria-disabled');
			}
		});
	}

	_onKeydown(event) {
		if (!event.target.closest(`[data-js-${NAMESPACE}-button]`)) return;

		const buttonElements = this.items.map((instance) => instance.elButton);

		const handlerConfig = {
			event,
			items: buttonElements,
			getTrigger: (el) => el,
			loop: true
		};

		if (this.config.orientation === 'horizontal') {
			horizontalKeyNavigationHandler(handlerConfig);
		} else {
			verticalKeyNavigationHandler(handlerConfig);
		}
	}
}

export default function factory(selector = DEFAULT_SELECTOR, settings = {}) {
	const listNode = document.querySelectorAll(selector);

	if (!listNode.length) return [];

	return Array.from(listNode).map((node) => {
		const config = nodeConfig(node, NAMESPACE, settings, DEFAULT_SETTINGS);
		const module = new Accordion(node, config);

		module.init();

		return module;
	});
}
